import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Sparkles, Loader2, CheckCircle } from 'lucide-react';

const ChatAssistant = ({ customers = [], onAction }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi! I\'m your VinCentrix AI Assistant 👋\n\nI understand natural language - just ask me in plain English! For example:\n\n• "Show me today\'s stats" or "How many customers do we have?"\n• "What are my hot leads?" or "Show priority customers"\n• "Any appointments today?" or "What\'s scheduled?"\n• "Find john" or "Search for sarah"\n• "Recent customers" or "Who just joined?"\n\nI can also help with:\n• Trade-ins and acquisition reports\n• Customer re-engagement strategies\n• Negotiation tips and market insights\n\nJust ask me anything in a natural way!'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const messagesEndRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const webSearch = async (query) => {
    try {
      console.log('🔍 Calling web search API:', query);
      const response = await fetch(`${BACKEND_URL}/api/ai-assistant/web-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query })
      });
      
      if (!response.ok) {
        throw new Error('Search API returned ' + response.status);
      }
      
      const data = await response.json();
      console.log('✅ Search results:', data);
      return data;
    } catch (error) {
      console.error('❌ Web search error:', error);
      return { 
        success: false, 
        error: error.message,
        results: []
      };
    }
  };
      

  const detectCommand = (userMessage) => {
    const msg = userMessage.toLowerCase();

    if (msg.includes('re-engage') || msg.includes('reengage')) {
      const nameMatch = userMessage.match(/re-?engage\s+([a-z\s]+)/i);
      if (nameMatch) {
        return { type: 'RE_ENGAGE', customerName: nameMatch[1].trim() };
      }
    }

    if (msg.includes('show') && (msg.includes('hot lead') || msg.includes('appointment'))) {
      if (msg.includes('hot')) return { type: 'FILTER', filter: 'hot-opportunity' };
      if (msg.includes('appointment')) return { type: 'FILTER', filter: 'appointment-set' };
    }

    if (msg.includes('schedule') && msg.includes('appointment')) {
      const nameMatch = userMessage.match(/schedule.*for\s+([a-z\s]+)/i);
      if (nameMatch) {
        return { type: 'SCHEDULE', customerName: nameMatch[1].trim() };
      }
    }

    if (msg.includes('find') || msg.includes('search')) {
      const nameMatch = userMessage.match(/(?:find|search)\s+(?:for\s+)?([a-z\s]+)/i);
      if (nameMatch) {
        return { type: 'FIND_CUSTOMER', customerName: nameMatch[1].trim() };
      }
    }

    if (msg.includes('details') || msg.includes('info about')) {
      const nameMatch = userMessage.match(/(?:details|info).*?(?:for|about)\s+([a-z\s]+)/i);
      if (nameMatch) {
        return { type: 'CUSTOMER_DETAILS', customerName: nameMatch[1].trim() };
      }
    }

    return null;
  };

  const executeCommand = async (command) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    try {
      switch (command.type) {
        case 'RE_ENGAGE': {
          const response = await fetch(`${API_URL}/api/customers`);
          const data = await response.json();
          const customer = data.customers?.find(c =>
            (c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim()).toLowerCase().includes(command.customerName.toLowerCase())
          );

          if (!customer) {
            return { message: `I couldn't find a customer named "${command.customerName}".` };
          }

          const dealership = localStorage.getItem('dealership') || localStorage.getItem('dealer');
          const dealershipId = dealership ? JSON.parse(dealership)?.id : 'champion-nissan';
          const engageResponse = await fetch(`${API_URL}/api/customers/${customer.id}/engage`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dealershipId })
          });
          const engageData = await engageResponse.json();

          if (engageData.success) {
            return {
              message: `✅ Successfully re-engaged ${customer.name}! AI has sent a new message.`,
              action: { type: 'SELECT_CUSTOMER', customerId: customer.id }
            };
          }

          return { message: `❌ Failed to re-engage ${customer.name}.` };
        }

        case 'FILTER': {
          return {
            message: `📊 Showing ${command.filter === 'hot-opportunity' ? 'hot opportunities' : 'customers with appointments'}...`,
            action: { type: 'APPLY_FILTER', filter: command.filter }
          };
        }

        case 'SCHEDULE': {
          const response = await fetch(`${API_URL}/api/customers`);
          const data = await response.json();
          const customer = data.customers?.find(c =>
            (c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim()).toLowerCase().includes(command.customerName.toLowerCase())
          );

          if (!customer) {
            return { message: `I couldn't find a customer named "${command.customerName}".` };
          }

          return {
            message: `📅 Opening schedule appointment form for ${customer.name}...`,
            action: { type: 'OPEN_SCHEDULE_MODAL', customerId: customer.id }
          };
        }

        case 'FIND_CUSTOMER':
        case 'CUSTOMER_DETAILS': {
          const response = await fetch(`${API_URL}/api/customers`);
          const data = await response.json();
          const customer = data.customers?.find(c =>
            (c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim()).toLowerCase().includes(command.customerName.toLowerCase())
          );

          if (!customer) {
            return { message: `I couldn't find a customer named "${command.customerName}".` };
          }

          if (command.type === 'FIND_CUSTOMER') {
            return {
              message:
                `✅ Found ${customer.name}!\n\n` +
                `📱 Phone: ${customer.phone || 'N/A'}\n` +
                `🚗 Vehicle: ${customer.vehicle || 'N/A'}\n` +
                `📊 Status: ${customer.acquisitionStatus || 'unknown'}\n` +
                `💰 Offer: ${customer.currentOffer || 'N/A'}`,
              action: { type: 'SELECT_CUSTOMER', customerId: customer.id }
            };
          }

          return {
            message:
              `📋 Details for ${customer.name}:\n\n` +
              `📱 Phone: ${customer.phone || 'N/A'}\n` +
              `📧 Email: ${customer.email || 'N/A'}\n` +
              `🚗 Vehicle: ${customer.vehicle || 'N/A'}\n` +
              `🔢 VIN: ${customer.vin || 'N/A'}\n` +
              `📏 Mileage: ${customer.mileage || 'N/A'}\n` +
              `📊 Status: ${customer.acquisitionStatus || 'unknown'}\n` +
              `💰 Current Offer: ${customer.currentOffer || 'N/A'}\n` +
              `👤 Assigned To: ${customer.assignedTo || 'Unassigned'}`,
            action: { type: 'OPEN_DETAIL_MODAL', customerId: customer.id }
          };
        }

        default:
          return { message: 'Command not recognized.' };
      }
    } catch (error) {
      console.error('Command execution error:', error);
      return { message: `❌ Error executing command: ${error.message}` };
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');

    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const command = detectCommand(userMessage);
      if (command) {
        setIsExecuting(true);
        const result = await executeCommand(command);
        setIsExecuting(false);

        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: result.message,
            action: result.action
          }
        ]);

        if (result.action && onAction) {
          onAction(result.action);
        }

        setIsLoading(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          message: userMessage, 
          customerId: null 
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      const assistantMessage = data.message || data.response || data.content || 'I received your message but got an unexpected response format.';
      
      setMessages([...newMessages, { role: 'assistant', content: assistantMessage }]);
    } catch (error) {
      console.error('AI Assistant Error:', error.message);
      
      let errorMessage = 'I encountered an error processing your request. ';
      if (error.message.includes('API request failed')) {
        errorMessage += 'The AI service is temporarily unavailable. ';
      } else if (error.message.includes('Network') || error.message.includes('fetch')) {
        errorMessage += 'There was a network error. Please check your connection. ';
      } else {
        errorMessage += error.message + '. ';
      }
      errorMessage += '\n\nI can still help you with commands like:\n\n• "Re-engage not-interested customers"\n• "Increase offers by 10%"\n• "Adjust offers for hot leads"\n\nOr try asking me again!';
      
      setMessages([...newMessages, { 
        role: 'assistant', 
        content: errorMessage
      }]);
    } finally {
      setIsLoading(false);
      setIsExecuting(false);
    }
  };

  const handleKeyPress = (e) => { 
    if (e.key === 'Enter' && !e.shiftKey) { 
      e.preventDefault(); 
      sendMessage(); 
    } 
  };

  return (
    <>
      {!isOpen && (
        <button onClick={() => setIsOpen(true)} className="chat-button">
          <MessageCircle size={22} />
          <div className="pulse-ring"></div>
        </button>
      )}

      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="chat-header-content">
              <div className="chat-icon-wrapper">
                <Sparkles size={18} />
              </div>
              <div>
                <h3 className="chat-title">VinCentrix AI</h3>
                <p className="chat-subtitle">Sales Assistant</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="close-button">
              <X size={20} />
            </button>
          </div>

          <div className="messages-container">
            {messages.map((msg, i) => (
              <div key={`chat-msg-${msg.timestamp || i}-${msg.role || 'unknown'}`} className={`message ${msg.role === 'user' ? 'message-user' : 'message-assistant'}`}>
                <div className="message-bubble">
                  <p className="message-text">{msg.content}</p>
                </div>
              </div>
            ))}
            
            {(isLoading || isExecuting) && (
              <div className="message message-assistant">
                <div className="message-bubble thinking-bubble">
                  <div className="thinking-content">
                    {isExecuting ? (
                      <>
                        <CheckCircle className="thinking-spinner" size={16} />
                        <span className="thinking-text">Executing command...</span>
                      </>
                    ) : (
                      <>
                        <Loader2 className="thinking-spinner" size={16} />
                        <span className="thinking-text">Thinking...</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          <div className="input-container">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Give me a command or ask a question..."
              disabled={isLoading || isExecuting}
              className="message-input"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || isLoading || isExecuting}
              className="send-button"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}

      <style>{`
        .chat-button {
          position: fixed;
          bottom: 24px;
          right: 24px;
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%);
          border: none;
          border-radius: 50%;
          color: white;
          box-shadow: 0 0 30px rgba(59, 130, 246, 0.6), 0 8px 25px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1000;
          animation: float 3s ease-in-out infinite;
        }

        .chat-button:hover {
          transform: scale(1.1) translateY(-2px);
          box-shadow: 0 12px 32px rgba(102, 126, 234, 0.5);
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }

        .pulse-ring {
          position: absolute;
          width: 100%;
          height: 100%;
          border: 3px solid rgba(102, 126, 234, 0.6);
          border-radius: 50%;
          animation: pulse 2s ease-out infinite;
        }

        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.5); opacity: 0; }
        }

        .chat-window {
          position: fixed;
          bottom: 24px;
          right: 24px;
          width: 400px;
          height: 600px;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 20%, rgba(255, 255, 255, 0.02) 50%, rgba(255, 255, 255, 0.05) 80%, rgba(255, 255, 255, 0.1) 100%);
          backdrop-filter: blur(40px) saturate(150%);
          -webkit-backdrop-filter: blur(40px) saturate(150%);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 24px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2);
          display: flex;
          flex-direction: column;
          z-index: 1000;
          animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .chat-header {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%);
          padding: 20px;
          display: flex;
          align-items: center;
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.5), 0 4px 15px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2);
          justify-content: space-between;
          position: relative;
          overflow: hidden;
        }

        .chat-header::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
          animation: shimmer 3s ease-in-out infinite;
        }

        @keyframes shimmer {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(10%, 10%); }
        }

        .chat-header-content {
          display: flex;
          align-items: center;
          gap: 12px;
          position: relative;
          z-index: 1;
        }

        .chat-icon-wrapper {
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .chat-title {
          font-size: 18px;
          font-weight: 700;
          color: white;
          margin: 0;
          letter-spacing: -0.5px;
        }

        .chat-subtitle {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.9);
          margin: 0;
          font-weight: 500;
        }

        .close-button {
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          position: relative;
          z-index: 1;
        }

        .close-button:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: rotate(90deg);
        }

        .messages-container {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
          background: linear-gradient(to bottom, #f8f9fc 0%, #ffffff 100%);
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .messages-container::-webkit-scrollbar {
          width: 6px;
        }

        .messages-container::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 3px;
        }

        .message {
          display: flex;
          animation: messageSlide 0.3s ease-out;
        }

        @keyframes messageSlide {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .message-user {
          justify-content: flex-end;
        }

        .message-assistant {
          justify-content: flex-start;
        }

        .message-bubble {
          max-width: 85%;
          padding: 12px 16px;
          border-radius: 16px;
          position: relative;
        }

        .message-user .message-bubble {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-bottom-right-radius: 4px;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .message-assistant .message-bubble {
          background: white;
          color: #2d3748;
          border: 1px solid #e2e8f0;
          border-bottom-left-radius: 4px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .thinking-bubble {
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
          border: 1px solid rgba(102, 126, 234, 0.2);
          animation: thinkingPulse 1.5s ease-in-out infinite;
        }

        @keyframes thinkingPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.02); opacity: 0.9; }
        }

        .thinking-content {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #667eea;
          font-weight: 500;
        }

        .thinking-spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .thinking-text {
          font-size: 14px;
        }

        .message-text {
          font-size: 14px;
          line-height: 1.6;
          margin: 0;
          white-space: pre-wrap;
        }

        .input-container {
          padding: 16px;
          background: white;
          border-top: 1px solid #e2e8f0;
          display: flex;
          gap: 12px;
        }

        .message-input {
          flex: 1;
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 14px;
          outline: none;
          transition: all 0.2s;
          background: #f8f9fc;
        }

        .message-input:focus {
          border-color: #667eea;
          background: white;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .message-input:disabled {
          background: #f1f5f9;
          cursor: not-allowed;
        }

        .send-button {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          border-radius: 12px;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .send-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(102, 126, 234, 0.3);
        }

        .send-button:disabled {
          background: #cbd5e1;
          cursor: not-allowed;
          opacity: 0.5;
        }
      `}</style>
    </>
  );
};

export default ChatAssistant;
